/**
 * The Backup board — NOVA's fifth board, and the one that replaces a manual
 * daily ritual rather than a missing dashboard.
 *
 * Today two senior engineers alternate weeks exporting Veeam errors to Excel
 * and typing them into a workbook — roughly 15–30 minutes every working day
 * since 2020. The transcription is the part being automated. The judgement (a
 * failure acknowledged, a cause written down) stays, and moves into the L2
 * drawer.
 *
 * Three things this board can do that the workbook structurally cannot
 * (concept §2):
 *
 * - **See a job that never started.** The manual export lists *errors*; a job
 *   that silently stopped being scheduled produces no error, so it produces no
 *   row. "Did not run" is its own L0 tile here.
 * - **Prove coverage.** The coverage section joins every server against every
 *   protection source, so an unprotected server surfaces the day it appears.
 * - **Tell silence from health.** When collection stops, the tiles read
 *   unknown, not green.
 *
 * Two collection paths, one grammar: Veeam is pushed from an on-premises
 * collector every 15 minutes, Azure Backup is pulled on the Resource Graph
 * poller NOVA already runs. The `source` filter scopes the L1 sections; the L0
 * tiles stay estate-wide across sources, because "backed up by something" is
 * the question that matters.
 */

import { Icon, RefreshCw } from "@norican/icons";
import { Alert, Button, PageHeader, Skeleton } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { FilterRow, useBoardRange } from "@/components/board";
import { NAV_SECTIONS } from "@/components/nav-sections";
import {
  useBackupActions,
  useBackupCoverage,
  useBackupJobs,
  useBackupOverview,
  useBackupSites,
  useBackupSlaCompliance,
} from "@/lib/api";
import { parseListParam } from "@/lib/drill";
import { logger } from "@/lib/logger";

import { ActionsSection } from "./actions-section";
import { BackupSetupPanel, CollectorBanner, CollectorFreshness } from "./collector-banner";
import { CoverageSection } from "./coverage-section";
import { FailuresSection } from "./failures-section";
import { BackupKpiStrip } from "./kpi-strip";
import { SITE_PARAM, SOURCE_PARAM } from "./params";
import { RepositoriesSection } from "./repositories-section";
import { SiteHeatStrip } from "./site-heat-strip";
import { SlaComplianceSection } from "./sla-compliance-section";
import { SlaSection } from "./sla-section";
import { sourceLabel } from "./status";

const SECTION = NAV_SECTIONS.find((section) => section.label === "Backup");

/** The heat strip is a day grid; more than a quarter of them stops being readable. */
const MAX_HISTORY_DAYS = 90;

export { BackupActionsPage } from "./actions-page";
export { BackupCoveragePage } from "./coverage-page";
export { BackupJobsPage } from "./jobs-page";
export { BACKUP_COVERAGE_PATH, BACKUP_JOBS_PATH, BACKUP_PATH } from "./params";

export function BackupPage() {
  const queryClient = useQueryClient();
  const range = useBoardRange();
  const [searchParams] = useSearchParams();
  const sourceIds = parseListParam(searchParams.get(SOURCE_PARAM));
  const siteIds = parseListParam(searchParams.get(SITE_PARAM));

  const overview = useBackupOverview();
  const actionList = useBackupActions();
  const jobs = useBackupJobs();
  const sites = useBackupSites(Math.min(range.days, MAX_HISTORY_DAYS));
  const coverage = useBackupCoverage();
  // The compliance window follows the board's range picker, which is honest
  // here in a way it is not for the current-state panels: "are we meeting the
  // target" is a question about a period, so narrowing the period genuinely
  // changes the answer. Capped like the heat strip, since both read the same
  // run history.
  const sla = useBackupSlaCompliance(Math.min(range.days, MAX_HISTORY_DAYS));

  const data = overview.data;
  const refreshing =
    overview.isFetching ||
    jobs.isFetching ||
    sites.isFetching ||
    sla.isFetching ||
    coverage.isFetching;

  const refresh = () => {
    logger.debug("[backup] manual refresh requested");
    void queryClient.invalidateQueries({ queryKey: ["backup"] });
  };

  // Filter options are built from the FULL payloads, never a filtered slice, so
  // narrowing to one source cannot make the other unpickable (design rules §3.3).
  const sourceOptions = (data?.sources ?? []).map((summary) => ({
    value: summary.source,
    label: sourceLabel(summary.source),
  }));
  const siteOptions = [...new Set((sites.data?.sites ?? []).map((site) => site.site))]
    .sort()
    .map((site) => ({ value: site, label: site }));

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Backup"
        subtitle={
          SECTION?.description ??
          "Veeam and Azure Backup under one grammar: what ran, what failed, what did not run at all, and what is protected by nothing."
        }
        actions={
          <div className="flex flex-wrap items-center gap-4">
            {(data?.collectors ?? []).map((collector) => (
              <CollectorFreshness key={collector.source} collector={collector} />
            ))}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {overview.error && (
        <Alert tone="danger" title="Couldn't load the Backup board">
          {overview.error instanceof Error
            ? overview.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not a Veeam one."}
        </Alert>
      )}

      {overview.isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="24rem" />
        </div>
      ) : (
        data &&
        (!data.configured ? (
          <BackupSetupPanel statusDetail={data.status_detail} />
        ) : (
          <>
            <CollectorBanner overview={data} />

            {/* First, and above the KPI strip on purpose: the numbers say how
                the estate is, this says what to do about it. */}
            {actionList.data && <ActionsSection actions={actionList.data} />}

            {/* L0: the four numbers, unknown when collection has gone quiet. */}
            <BackupKpiStrip overview={data} />

            {/* One filter row, scoping every L1 section below it (§4.3). The
                tiles above stay estate-wide on purpose — "backed up by
                something" is the question, and a source filter must not be able
                to make the estate look healthier than it is.

                The date range drives the two panels that are genuinely about a
                *period* — the heat strip and the SLA success rate — and nothing
                else: current job state, SLA tiers and coverage are all *now*, so
                narrowing the range cannot honestly change them. Both windowed
                panels carry the day count in their own titles, so the
                connection is visible rather than inferred. */}
            <FilterRow
              dimensions={[
                { param: SOURCE_PARAM, label: "Source", noun: "source", options: sourceOptions },
                { param: SITE_PARAM, label: "Site", noun: "site", options: siteOptions },
              ]}
            />

            {sites.data && (
              <SiteHeatStrip sites={sites.data} sourceIds={sourceIds} siteIds={siteIds} />
            )}

            {jobs.data && (
              <FailuresSection
                jobs={jobs.data.jobs}
                stale={jobs.data.stale}
                sourceIds={sourceIds}
                siteIds={siteIds}
              />
            )}

            {jobs.data && (
              <SlaSection
                jobs={jobs.data.jobs}
                collectedAt={jobs.data.collected_at}
                sourceIds={sourceIds}
                siteIds={siteIds}
              />
            )}

            {/* The tier panel above says whether each tier is green now; this
                one says whether the promise has been kept over the window,
                which is the question an audit asks and the one the run history
                exists to answer. */}
            {sla.data && (
              <SlaComplianceSection compliance={sla.data} sourceIds={sourceIds} siteIds={siteIds} />
            )}

            {coverage.data && <CoverageSection coverage={coverage.data} />}

            <RepositoriesSection repositories={data.repositories} sourceIds={sourceIds} />
          </>
        ))
      )}
    </main>
  );
}
